// The import card in the platform agent's panel (D12): before a menu import or a copy runs, while it runs, and how it
// ended. Wording from the Amigo for menus design's copy sheet (Import card, Not live yet, Buttons, Importing, Failed,
// What to try, the 5 reasons, Went live, Import done).
export default {
  title: {
    import: 'Import the menu from {{platform}}',
    copy: 'Copy the menu from {{restaurant}}',
    data: 'Create the menu Amigo read',
  },
  // A menu Amigo read from a file or the chat (QA36): a change card with each dish, before it is created.
  contents: {
    title: 'Create {{categories}} and {{dishes}}',
    from: {
      file: 'Amigo read this from your file',
      chat: 'Amigo read this from our chat',
    },
    summary: 'Creates {{categories}} and {{dishes}}. Check each dish with its price in the list below.',
    price: 'Price',
    tax_category: 'Tax category',
    no_price: 'No price yet',
    default_tax: 'Your standard rate',
    // Once approved: the import's run, in the words of creating a menu.
    creating: 'Creating {{categories}} and {{dishes}}…',
    done: "The menu is created: {{categories}}, {{dishes}} and {{option_groups}} are on {{restaurant}}'s menu",
    done_plain: 'The menu is created',
  },
  waiting: 'Waiting for approval',
  labels: {
    from: 'From',
    to: 'To',
    menu: 'Menu',
    brings_over: 'Brings over',
  },
  from: {
    // "Lieferando · lieferando.de/speisekarte/pizzeria-lupo-koeln"
    platform: '{{platform}} · {{link}}',
    // "Pizzeria Lupo · 12 categories, 140 dishes"
    restaurant: '{{restaurant}} · {{categories}}, {{dishes}}',
    // "The menu Amigo read · 2 categories, 4 dishes" (B19)
    data: 'The menu Amigo read · {{categories}}, {{dishes}}',
  },
  to: {
    only_location: '{{name}}, its only location',
    brand_one: '{{name}} brand menu, its only location',
    brand_other: '{{name}} brand menu, all {{count}} locations',
  },
  menu: {
    empty: 'Empty, so this becomes the menu',
    adds: 'Adds to the current menu ({{size}}). Dishes with the same name are updated; nothing is removed.',
    // The menu's name and size: "Speisekarte: 12 categories, 140 dishes".
    size_named: '{{name}}: {{categories}}, {{dishes}}',
    size: '{{categories}}, {{dishes}}',
  },
  counts: {
    categories_one: '{{count}} category',
    categories_other: '{{count}} categories',
    dishes_one: '{{count}} dish',
    dishes_other: '{{count}} dishes',
    option_groups_one: '{{count}} option group',
    option_groups_other: '{{count}} option groups',
  },
  // What an import brings over, as one list: "Categories, dishes, prices, option groups, photos, and allergens and
  // nutrition where Lieferando has them". Each item starts lowercase; the list's first letter is capitalised.
  brings_over: {
    everything: 'Everything, as it is',
    categories: 'categories',
    dishes: 'dishes',
    descriptions: 'descriptions',
    prices: 'prices',
    option_groups: 'option groups',
    photos: 'photos',
    where: {
      allergens_nutrition: 'allergens and nutrition where {{platform}} has them',
      allergens: 'allergens where {{platform}} has them',
      nutrition: 'nutrition where {{platform}} has it',
    },
  },
  not_live: "{{restaurant}} isn't live yet: guests can see the menu on the shop link but can't order.",
  buttons: {
    import: 'Import',
    copy: 'Copy',
    cancel: 'Cancel',
  },
  hint: 'Or reply below to change something.',
  action_failed: "That didn't go through, so nothing changed. Try again in a moment.",
  // Shop's 409 codes when Import is pressed again while the first one runs (QA125).
  refused: {
    already_running: 'This import is already running. Amigo tells you here when it is done.',
    already_applying: 'This is being applied right now. Wait a moment until it is done.',
  },
  importing: {
    title: {
      import: 'Importing from {{platform}}…',
      copy: 'Copying from {{restaurant}}…',
    },
    started: 'Started {{time}}',
    queued: 'Starting…',
    hint: "This can take a few minutes. You can close the panel or keep working; I'll post the result here.",
  },
  done: {
    // What is on the menu now, while the card is the only result ({{categories}} … are `counts`): "The import from
    // Lieferando is done: 12 categories, 140 dishes and 38 option groups are on Pizzeria Lupo's menu".
    title: {
      import:
        "The import from {{platform}} is done: {{categories}}, {{dishes}} and {{option_groups}} are on {{restaurant}}'s menu",
      copy: 'The copy is done: {{categories}}, {{dishes}} and {{option_groups}} are on the menu',
    },
    // Without counts, or once Amigo's result under the card says them.
    title_plain: {
      import: 'The import from {{platform}} is done',
      copy: 'The copy is done',
    },
    finished: 'Finished {{time}}',
    // The page in the sidebar's words: "Open Menu → Manage menus".
    open: 'Open {{page}}',
  },
  failed: {
    title: {
      import: "Couldn't import: {{reason}}",
      copy: "Couldn't copy: {{reason}}",
    },
    title_plain: {
      import: "Couldn't import",
      copy: "Couldn't copy",
    },
    nothing: {
      import: 'Nothing was imported.',
      copy: 'Nothing was copied.',
    },
    what_to_try: 'What to try:',
    retry: 'Try again',
    reasons: {
      listing_not_found: {
        title: 'listing not found',
        text: '{{platform}} has no restaurant at this link. Nothing was imported.',
        try: "Open the link in a browser to check it shows the restaurant's menu, then send it again.",
      },
      listing_not_supported: {
        title: 'listing not supported',
        text: "This is a {{platform}} chain page, not one restaurant's menu, so I can't import it. Nothing was imported.",
        try: "Send the link to the restaurant's own menu page on {{platform}}.",
      },
      platform_failed: {
        title: '{{platform}} failed',
        text: "{{platform}} didn't answer, so nothing was imported.",
        try: 'Try again in a few minutes.',
      },
      // A copy from another restaurant that failed on our side: there is no platform to name.
      copy_failed: {
        title: 'the copy failed',
        text: "The copy didn't finish, so nothing was copied.",
        try: 'Try again in a few minutes.',
      },
      // {{listing}} and {{currency}} are currencies with their sign: "British pounds (£)", "euros (€)". {{page}} is the
      // sidebar's My info.
      currency_mismatch: {
        title: "the currency doesn't match",
        text: 'The listing is in {{listing}}, but {{restaurant}} uses {{currency}}. Nothing was imported.',
        try: "Send a listing in {{currency}}, or check the restaurant's currency in {{page}} first.",
        text_other: "The listing is in another currency than {{restaurant}}'s. Nothing was imported.",
        try_other: "Send a listing in the restaurant's currency, or check its currency in {{page}} first.",
      },
      link_paused: {
        title: 'link paused',
        text: 'This link failed 3 times in a row, so imports from it are paused until {{time}}. Nothing was imported.',
        text_no_until:
          'This link failed 3 times in a row, so imports from it are paused for now. Nothing was imported.',
        try: 'Send a link from another platform, or copy the menu from another restaurant.',
      },
    },
  },
  stopped: {
    title: 'Stopped: {{restaurant}} went live',
    nothing: {
      import: 'Nothing was imported',
      copy: 'Nothing was copied',
    },
  },
  // The card folded to one line.
  line: {
    cancelled: 'Cancelled',
    expired: 'Expired',
    replaced: 'Replaced',
  },
  // The message the panel sends the agent with the card's outcome (contracts §5 clientContext.changeSet).
  message: {
    approved: 'Approved.',
    cancelled: 'Cancelled.',
    retried: 'Tried again.',
  },
};
